'use client';

import React, { useState } from 'react';
import moment from 'moment';
import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	AccordionItem,
} from 'reactstrap';
import type { ActivityLogEntry, LumiConversationResponse } from 'src/types';
import { toast } from 'react-toastify';
import { fetchLumiConversation } from 'src/redux/jobs/api';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import type { MediaItem } from 'src/types/quote-approval';
import { wrapStringWithLink } from 'src/helpers/message-utils';



const LumiStarIcon: React.FC = () => (
	<svg
		viewBox="0 0 17.8667 17.8667"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		width="20"
		height="20"
	>
		<path
			d="M2.68333 17.2667V13.1M2.68333 4.76667V0.6M0.6 2.68333H4.76667M0.6 15.1833H4.76667M9.76667 1.43333L8.32152 5.19072C8.08651 5.80174 7.969 6.10726 7.78627 6.36424C7.62432 6.592 7.42533 6.79099 7.19757 6.95294C6.94059 7.13567 6.63508 7.25318 6.02405 7.48819L2.26667 8.93333L6.02405 10.3785C6.63508 10.6135 6.94059 10.731 7.19757 10.9137C7.42533 11.0757 7.62432 11.2747 7.78628 11.5024C7.969 11.7594 8.08651 12.0649 8.32152 12.6759L9.76667 16.4333L11.2118 12.6759C11.4468 12.0649 11.5643 11.7594 11.7471 11.5024C11.909 11.2747 12.108 11.0757 12.3358 10.9137C12.5927 10.731 12.8983 10.6135 13.5093 10.3785L17.2667 8.93333L13.5093 7.48818C12.8983 7.25317 12.5927 7.13567 12.3358 6.95294C12.108 6.79099 11.909 6.592 11.7471 6.36424C11.5643 6.10726 11.4468 5.80174 11.2118 5.19072L9.76667 1.43333Z"
			stroke="#7B61FF"
			strokeWidth="1.2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const ResidentHomeIcon: React.FC = () => (
	<svg
		viewBox="0 0 16.2001 16.811"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		width="20"
		height="20"
	>
		<path
			d="M5.60006 16.2109V10.0443C5.60006 9.57757 5.60006 9.34422 5.69089 9.16596C5.77078 9.00916 5.89826 8.88167 6.05507 8.80178C6.23332 8.71095 6.46668 8.71095 6.93339 8.71095H9.26672C9.73343 8.71095 9.96679 8.71095 10.145 8.80178C10.3019 8.88167 10.4293 9.00916 10.5092 9.16596C10.6001 9.34422 10.6001 9.57757 10.6001 10.0443V16.2109M7.28147 1.01429L1.62955 5.41023C1.25174 5.70408 1.06284 5.85101 0.926746 6.03501C0.806195 6.198 0.716392 6.38161 0.661747 6.57684C0.600057 6.79723 0.600057 7.03654 0.600057 7.51517V13.5443C0.600057 14.4777 0.600057 14.9444 0.781713 15.3009C0.941502 15.6145 1.19647 15.8695 1.51007 16.0293C1.86659 16.2109 2.3333 16.2109 3.26672 16.2109H12.9334C13.8668 16.2109 14.3335 16.2109 14.69 16.0293C15.0036 15.8695 15.2586 15.6145 15.4184 15.3009C15.6001 14.9444 15.6001 14.4777 15.6001 13.5443V7.51517C15.6001 7.03654 15.6001 6.79723 15.5384 6.57684C15.4837 6.38161 15.3939 6.198 15.2734 6.03501C15.1373 5.85101 14.9484 5.70408 14.5706 5.41023L8.91864 1.01429C8.62587 0.786584 8.47949 0.672728 8.31785 0.628963C8.17522 0.590346 8.02489 0.590346 7.88227 0.628963C7.72063 0.672728 7.57424 0.786584 7.28147 1.01429Z"
			stroke="#101828"
			strokeWidth="1.2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

interface LumiConversationProps {
	logDetails: ActivityLogEntry;
}

const LumiConversation: React.FC<LumiConversationProps> = ({ logDetails }) => {
	const dispatch = useAppDispatch();
	const timeZone = useAppSelector(
		(state) => state?.jobDetails?.data?.timezone,
	) as string | undefined;
	const [open, setOpen] = useState('');
	const [conversationData, setConversationData] =
		useState<LumiConversationResponse | null>(null);
	const [loadingConversation, setLoadingConversation] = useState(false);

	const photoCount = logDetails.event_attributes.media?.length ?? 0;

	const handleImageClick = (index: number) => {
		if (!conversationData?.images?.length) return;
		const collection: MediaItem[] = conversationData.images.map((img) => ({
			attachment_id: Number(img.id),
			job_media_id: Number(img.id),
			thumbnail_url: img.url,
			url: img.url,
			type: 'image',
			has_completed: true,
		}));
		dispatch(
			updateVisibility({
				modalType: 'imageSlidesModal',
				isVisible: true,
				extraProps: {
					modalData: {
						collection,
						position: index,
						showDeleteButton: false,
						imageType: 'lumi-intake-photos',
						timeZone,
					},
				},
			}),
		);
	};

	const handleConversationToggle = async () => {
		const opening = open !== '1';
		setOpen(opening ? '1' : '');

		const lumiRecordId = logDetails?.event_attributes?.lumi_record_id;
		if (opening && !conversationData && !loadingConversation && lumiRecordId) {
			setLoadingConversation(true);
			dispatch(fetchLumiConversation(lumiRecordId)({}))
				.unwrap()
				.then((res) => {
					setConversationData((res as {data:LumiConversationResponse}).data);
				})
				.catch((e: Record<string, unknown>) => {
					toast((e?.message as string) || 'Something went wrong', { type: 'error' });
				})
				.finally(() => {
					setLoadingConversation(false);
				});
		}
	};

	const renderConversation = () => {
		if (loadingConversation) {
			return (
				<p className="lumi-intake-card__value lumi-intake-card__loading">
					Loading conversation...
				</p>
			);
		}

		if (!conversationData) return null;

		const residentName = conversationData.resident?.name || 'Resident';
		const dateLabel = conversationData.metadata?.created_at
			? moment(conversationData.metadata.created_at).format('dddd, MMM D')
			: null;

		const sortedMessages = [...conversationData.messages].sort(
			(a, b) => a.order - b.order,
		);

		return (
			<>
				{dateLabel && (
					<div className="lumi-intake-card__chat-date">{dateLabel}</div>
				)}
				{sortedMessages.map((msg) => (
					<React.Fragment key={msg.order}>
						<div className="lumi-intake-card__chat-row lumi-intake-card__chat-row--lumi">
							<div className="lumi-intake-card__chat-avatar lumi-intake-card__chat-avatar--lumi">
								<LumiStarIcon />
							</div>
							<div className="lumi-intake-card__chat-content lumi-intake-card__chat-content--lumi">
								<div className="lumi-intake-card__chat-bubble lumi-intake-card__chat-bubble--lumi">
									<p className="lumi-intake-card__chat-text">{wrapStringWithLink({ message: msg.question })}</p>
									<span className="lumi-intake-card__chat-time">
										{moment(conversationData.metadata.created_at).format(
											'h:mmA',
										)}
									</span>
								</div>
							</div>
						</div>

						{msg.answer && (
							<div className="lumi-intake-card__chat-row lumi-intake-card__chat-row--resident">
								<div className="lumi-intake-card__chat-avatar lumi-intake-card__chat-avatar--resident">
									<ResidentHomeIcon />
								</div>
								<div className="lumi-intake-card__chat-content lumi-intake-card__chat-content--resident">
									<span className="lumi-intake-card__chat-name">
										{residentName}
									</span>
									<div className="lumi-intake-card__chat-bubble lumi-intake-card__chat-bubble--resident">
										<p className="lumi-intake-card__chat-text">{wrapStringWithLink({ message: msg.answer })}</p>
										<span className="lumi-intake-card__chat-time">
											{moment(conversationData.metadata.created_at).format(
												'h:mmA',
											)}
										</span>
									</div>
								</div>
							</div>
						)}
					</React.Fragment>
				))}
				{conversationData.images && conversationData.images.length > 0 && (() => {
					const images = conversationData.images;
					const isSingle = images.length === 1;
					const visibleImages = images.slice(0, 4);
					const overflowCount = images.length - 4;
					return (
						<div className="lumi-intake-card__chat-row lumi-intake-card__chat-row--resident">
							<div className="lumi-intake-card__chat-avatar lumi-intake-card__chat-avatar--resident">
								<ResidentHomeIcon />
							</div>
							<div className="lumi-intake-card__chat-content lumi-intake-card__chat-content--resident">
								<span className="lumi-intake-card__chat-name">{residentName}</span>
								<div className="lumi-intake-card__chat-bubble lumi-intake-card__chat-bubble--resident lumi-intake-card__chat-bubble--photos">
									<div className={`lumi-intake-card__chat-photos${isSingle ? ' lumi-intake-card__chat-photos--single' : ' lumi-intake-card__chat-photos--grid'}`}>
										{visibleImages.map((img, i) => {
											const isLast = i === 3 && overflowCount > 0;
											return (
												<button key={img.id} type="button" className="lumi-intake-card__chat-photo-wrap" onClick={() => handleImageClick(i)}>
												<img src={img.url} alt="" className="lumi-intake-card__chat-photo" />
													{isLast && (
														<div className="lumi-intake-card__chat-photo-overflow">+{overflowCount}</div>
													)}
												</button>
											);
										})}
									</div>
									<span className="lumi-intake-card__chat-time lumi-intake-card__chat-time--photos">
										{moment(conversationData.metadata.created_at).format('h:mmA')}
									</span>
								</div>
							</div>
						</div>
					);
				})()}
			</>
		);
	};

	return (
		<Accordion
			open={open}
			toggle={handleConversationToggle}
			className="lumi-intake-card__accordion"
		>
			<AccordionItem>
				<AccordionHeader targetId="1">
					<div className="lumi-intake-card__conversation-toggle-left">
						<span className="lumi-intake-card__conversation-toggle-title">
							Full Conversation History
						</span>
						{photoCount > 0 && (
						<div className="lumi-intake-card__conversation-toggle-meta">
							<span>{photoCount} {photoCount === 1 ? 'photo' : 'photos'}</span>
						</div>
						)}
					</div>
					<span
						className={`lumi-intake-card__chevron ${open !== '1' ? 'lumi-intake-card__chevron--open' : ''}`}
					>
						&#8964;
					</span>
				</AccordionHeader>
				<AccordionBody
					accordionId="1"
					className="lumi-intake-card__conversation"
				>
					{renderConversation()}
				</AccordionBody>
			</AccordionItem>
		</Accordion>
	);
};

export default LumiConversation;
